import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

type AnimationVariant = 'fade' | 'fadeUp' | 'fadeDown' | 'scale' | 'slideRight';

export interface AnimatedContainerProps {
  children: React.ReactNode;
  /** Visual entrance style. Defaults to a subtle fade + rise. */
  variant?: AnimationVariant;
  /** Seconds before the animation starts. */
  delay?: number;
  /** Seconds the animation runs. */
  duration?: number;
  className?: string;
  style?: React.CSSProperties;
  /** Stagger children when each child is itself a motion element. */
  stagger?: boolean;
}

const VARIANTS: Record<AnimationVariant, { hidden: any; visible: any }> = {
  fade: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeUp: {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
  },
  fadeDown: {
    hidden: { opacity: 0, y: -16 },
    visible: { opacity: 1, y: 0 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.96 },
    visible: { opacity: 1, scale: 1 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  },
};

/**
 * Presentation-only wrapper that adds a smooth entrance animation via
 * Framer Motion. Safe to wrap around any content; it does not alter children.
 */
const AnimatedContainer: React.FC<AnimatedContainerProps> = ({
  children,
  variant = 'fadeUp',
  delay = 0,
  duration = 0.35,
  className,
  style,
  stagger = false,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const selected = VARIANTS[variant] ?? VARIANTS.fadeUp;

  if (stagger) {
    return (
      <motion.div
        className={className}
        style={style}
        initial="hidden"
        animate="visible"
        variants={{
          visible: {
            transition: { staggerChildren: 0.06, delayChildren: delay },
          },
        }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      className={className}
      style={{ minHeight: '100%', ...style }}
      initial={shouldReduceMotion ? false : 'hidden'}
      animate="visible"
      variants={selected}
      transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default AnimatedContainer;
